export interface Project {
  id: string;
  title: string;
  slug: string;
  category: 'Residential' | 'Commercial' | 'Cultural' | 'Interior' | 'Adaptive Reuse';
  year: number;
  location: string;
  status: 'Completed' | 'Under Construction' | 'In Progress' | 'Concept';
  area: string;
  heroImage: string;
  gallery: string[];
  blueprintImage?: string;
  videoUrl?: string;
  shortDescription: string;
  narrative: string;
  specs: {
    client?: string;
    typology: string;
    structuralSystem: string;
    materials: string[];
    leadArchitect?: string;
    siteArea?: string;
    awards?: string[];
  };
}

export interface StudioInfo {
  name: string;
  founder: string;
  title: string;
  portraitImage?: string;
  location: string;
  coordinates: string;
  email: string;
  phone: string;
  instagram: string;
  linkedin: string;
  tagline: string;
  philosophySummary: string;
  philosophyFull: string[];
  stats: {
    label: string;
    value: string;
    detail: string;
  }[];
}

export interface DesignPhase {
  number: string;
  title: string;
  subtitle: string;
  description: string;
  deliverables: string[];
}

export interface Service {
  id: string;
  number: string;
  name: string;
  description: string;
  highlights: string[];
}

export interface Award {
  year: string;
  award: string;
  project: string;
  category: string;
}

// -------------------------------------------------------------
// STUDIO & ARCHITECT PROFILE DATA
// (Easy to edit when brother provides his bio and details)
// -------------------------------------------------------------
export const studioData: StudioInfo = {
  name: "DOMINIC \"HUTCH\" GLARINO",
  founder: "Arch. Dominic \"Hutch\" Glarino, uap",
  title: "Principal Architect & Spatial Designer",
  portraitImage: "/assets/profile.jpg",
  location: "Metro Manila & Available Worldwide",
  coordinates: "LAT 14°35'14\" N / LONG 121°01'44\" E • ELEV 42M",
  email: "studio@architectdominic.com",
  phone: "+63 (917) 000-ARCH",
  instagram: "@dominic.glarino.arch",
  linkedin: "linkedin.com/in/dominic-glarino",
  tagline: "Tectonic Clarity. Human Scale. Climate Resonance.",
  philosophySummary: 
    "Architecture is not merely the enclosing of space—it is an act of dialogue between topography, tactile materiality, natural light, and the daily rituals of the human inhabitant.",
  philosophyFull: [
    "We believe architecture should age with grace, adopting materials that patina rather than perish: cast-in-place board-marked concrete, charred timber, local volcanic stone, and expansive thermal glass.",
    "Every volume begins with climate and site conditions. We harness prevailing cross-ventilation, deep overhangs for tropical sun management, and indoor-outdoor courtyards that dissolve boundaries between shelter and landscape.",
    "Our studio operates at the nexus of rigorous technical craft and poetic restraint, delivering residences and commercial landmarks that feel grounded, timeless, and quietly monumental."
  ],
  stats: [
    { label: "Built Works", value: "24+", detail: "Across residential & commercial" },
    { label: "Floor Area", value: "18.5k", detail: "Square meters designed & built" },
    { label: "Design Awards", value: "07", detail: "National & regional architectural honors" },
    { label: "Climate Responsive", value: "100%", detail: "Passive solar & natural ventilation" }
  ]
};

// -------------------------------------------------------------
// PORTFOLIO PROJECTS
// (Curated with high-res architectural photography & blueprints;
// easily swappable when actual project files are received)
// -------------------------------------------------------------
export const portfolioProjects: Project[] = [
  {
    id: "01",
    title: "Monolith Pavilion Residence",
    slug: "monolith-pavilion",
    category: "Residential",
    year: 2024,
    location: "Antipolo Ridges, Rizal",
    status: "Completed",
    area: "620 m²",
    heroImage: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=85",
    gallery: [
      "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1600566753376-12c8ab7fb75b?auto=format&fit=crop&w=1600&q=85"
    ],
    blueprintImage: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=85",
    videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
    shortDescription: "A cantilevered concrete and glass residential pavilion hovering above a lush tropical ravine.",
    narrative: 
      "Conceived as a dialogue between earthbound mass and aerial lightness, the Monolith Pavilion frames panoramic views of the Sierra Madre skyline. Raw board-formed concrete walls anchor the structure to bedrock, while a 6-meter post-tensioned cantilever extends the main living plane out into the tree canopy. Continuous floor-to-ceiling double-glazed panels slide away entirely into hidden pockets, creating seamless cross-ventilation that eliminates the need for active daytime cooling.",
    specs: {
      typology: "Private Single-Family Residence",
      structuralSystem: "Reinforced Board-Formed Concrete & Steel Cantilever Truss",
      materials: ["Architectural Concrete", "Sustainably Harvested Teak", "Low-E Thermal Glass", "Basalt Stone"],
      siteArea: "1,450 m²",
      leadArchitect: "Arch. Dominic \"Hutch\" Glarino",
      awards: ["Excellence in Tropical Residential Design (2024)"]
    }
  },
  {
    id: "02",
    title: "Komorebi Timber Sanctuary",
    slug: "komorebi-sanctuary",
    category: "Residential",
    year: 2024,
    location: "Tagaytay Highlands",
    status: "Completed",
    area: "440 m²",
    heroImage: "https://images.unsplash.com/photo-1600585152220-90363fe7e115?auto=format&fit=crop&w=1920&q=85",
    gallery: [
      "https://images.unsplash.com/photo-1600585152220-90363fe7e115?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1600573472550-8090b5e0745e?auto=format&fit=crop&w=1600&q=85"
    ],
    blueprintImage: "https://images.unsplash.com/photo-1581291518857-4e27b48ff24e?auto=format&fit=crop&w=1600&q=85",
    shortDescription: "A minimalist timber frame retreat celebrating dappled sunlight, courtyards, and tactile joinery.",
    narrative: 
      "Named after the Japanese word for sunlight filtering through tree leaves, the Komorebi Sanctuary utilizes an exposed engineered glulam timber skeleton arranged around a central meditative water court. Operable timber louvers modulate tropical rain squalls and diffuse high-noon glare into soft ambient warmth across honed travertine floors.",
    specs: {
      typology: "Weekend Retreat / Residence",
      structuralSystem: "Mass Engineered Timber (Glulam) & Stone Foundation",
      materials: ["Engineered Pine Glulam", "Honed Roman Travertine", "Charred Shou Sugi Ban Cladding", "Corten Steel"],
      siteArea: "980 m²",
      leadArchitect: "Arch. Dominic \"Hutch\" Glarino"
    }
  },
  {
    id: "03",
    title: "Apex Cultural & Design Hub",
    slug: "apex-cultural-hub",
    category: "Cultural",
    year: 2023,
    location: "Bonifacio Global City",
    status: "Completed",
    area: "2,850 m²",
    heroImage: "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1920&q=85",
    gallery: [
      "https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=85"
    ],
    blueprintImage: "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1600&q=85",
    videoUrl: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4",
    shortDescription: "A geometric civic gallery and workshop center with perforated anodized aluminum brise-soleil.",
    narrative: 
      "Commissioned as a multi-disciplinary venue for contemporary arts, design discourse, and public exhibitions, Apex intervenes in the dense urban fabric with a dynamic origami-inspired facade. A parametric perforated metal veil wraps the northern light scoop, illuminating 12-meter high gallery voids without thermal gain.",
    specs: {
      typology: "Public Cultural Gallery & Amphitheater",
      structuralSystem: "Composite Steel Framing & Post-Tensioned Concrete Decks",
      materials: ["Perforated Anodized Aluminum", "Polished Terrazzo", "Exposed Cast Concrete", "Acoustic Wood Slats"],
      siteArea: "1,800 m²",
      leadArchitect: "Arch. Dominic \"Hutch\" Glarino",
      awards: ["Architectural Review Civic Design Award Finalist"]
    }
  },
  {
    id: "04",
    title: "Vessel Courtyard House",
    slug: "vessel-courtyard-house",
    category: "Residential",
    year: 2023,
    location: "Ayala Alabang, Muntinlupa",
    status: "Completed",
    area: "510 m²",
    heroImage: "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1920&q=85",
    gallery: [
      "https://images.unsplash.com/photo-1600585154526-990dced4db0d?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1600607687920-4e2a09cf159d?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=1600&q=85"
    ],
    shortDescription: "A monolithic inward-looking sanctuary structured around three sunken internal gardens.",
    narrative: 
      "Designed for a busy urban family seeking complete stillness, the Vessel House presents an unadorned, blind volcanic stone perimeter to the suburban street. Once inside, the home explodes into light-wells, lush sunken ferns, cascading water planes, and glass corridors that frame changing cloud formations throughout the day.",
    specs: {
      typology: "Private Courtyard Residence",
      structuralSystem: "Dual-Wall Reinforced Concrete with Thermal Air Gap",
      materials: ["Unpolished Adobe Stone", "Charred Cedar", "Brushed Brass Hardware", "Reflective Black Granite"],
      siteArea: "820 m²",
      leadArchitect: "Arch. Dominic \"Hutch\" Glarino"
    }
  },
  {
    id: "05",
    title: "The Raw Foundry (Adaptive Reuse)",
    slug: "raw-foundry-adaptive-reuse",
    category: "Adaptive Reuse",
    year: 2024,
    location: "Escolta Historic District, Manila",
    status: "Under Construction",
    area: "1,600 m²",
    heroImage: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1920&q=85",
    gallery: [
      "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1497366811353-6870744d04b2?auto=format&fit=crop&w=1600&q=85"
    ],
    shortDescription: "Transforming a 1930s industrial warehouse into an open-air creative atelier and cafe.",
    narrative: 
      "A sensitive conservation effort that strips away decades of makeshift partitions to reveal the building's historic riveted steel trusses and unreinforced masonry arches. Modern glass insertions and a floating mezzanine introduce contemporary code compliance while honoring historic patina.",
    specs: {
      typology: "Commercial / Creative Studio & Cafe",
      structuralSystem: "Seismic Retrofit Steel Bracing & Restored Heritage Masonry",
      materials: ["Original Clay Brick", "Structural Carbon Steel", "Industrial Fluted Glass", "Microcement Flooring"],
      siteArea: "950 m²",
      leadArchitect: "Arch. Dominic \"Hutch\" Glarino"
    }
  },
  {
    id: "06",
    title: "Terra Workplace & Showroom",
    slug: "terra-workplace",
    category: "Commercial",
    year: 2023,
    location: "Makati Central Business District",
    status: "Completed",
    area: "1,120 m²",
    heroImage: "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1920&q=85",
    gallery: [
      "https://images.unsplash.com/photo-1497366754035-f200968a6e72?auto=format&fit=crop&w=1600&q=85",
      "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1600&q=85"
    ],
    shortDescription: "A biophilic headquarters crafted with rammed earth walls, warm acoustic felts, and circadian illumination.",
    narrative: 
      "Rejecting sterile corporate aesthetics, the Terra headquarters utilizes localized rammed earth partitions that stabilize indoor humidity and acoustic reverberation. Modular workspace neighborhoods cluster along perimeter daylight zones.",
    specs: {
      typology: "Corporate Headquarters & Design Gallery",
      structuralSystem: "Internal Steel Sub-Frame & Non-Loadbearing Rammed Earth Partitions",
      materials: ["Stabilized Rammed Earth", "Recycled Acoustic Felt", "American Walnut", "Textured Linen"],
      siteArea: "1,120 m² Floor Plate",
      leadArchitect: "Arch. Dominic \"Hutch\" Glarino"
    }
  }
];

// -------------------------------------------------------------
// DESIGN PROCESS / PHILOSOPHY PHASES
// -------------------------------------------------------------
export const designPhases: DesignPhase[] = [
  {
    number: "01",
    title: "Context & Microclimate Study",
    subtitle: "Site, Wind, Solar Angles & Topography",
    description: 
      "Before sketching a single wall, we listen to the land. We record solar trajectories, prevailing monsoons, soil strata, and surrounding vegetation to establish passive environmental parameters.",
    deliverables: ["Solar & Shading Analysis", "Microclimate & Wind Vector Map", "Zoning & Topographical Model"]
  },
  {
    number: "02",
    title: "Volumetric & Schematic Massing",
    subtitle: "Spatial Hierarchy & Circulation",
    description: 
      "We sculpt the primary architectural volumes. Through physical models, parametric 3D studies, and sightline diagrams, we explore relationships between private retreats and communal gathering.",
    deliverables: ["3D Massing Studies", "Functional Circulation Diagrams", "Initial Spatial Concept Renders"]
  },
  {
    number: "03",
    title: "Tectonic Detailing & Materiality",
    subtitle: "Joinery, Textures, Light & Engineering",
    description: 
      "True architecture lives in the joint. We specify materials that age honorably and coordinate structural, mechanical, and lighting disciplines down to the millimeter.",
    deliverables: ["Comprehensive Construction Sets", "Custom Millwork & Detail Callouts", "Material Tactile Board"]
  },
  {
    number: "04",
    title: "Craft & Construction Oversight",
    subtitle: "On-Site Supervision & Rigorous Quality",
    description: 
      "We partner directly with master artisans, structural engineers, and general contractors on site, verifying that cast concrete mixes, timber joinery, and glazing align flawlessly with design intent.",
    deliverables: ["Weekly Site Inspections", "Material Mockup Review", "As-Built Archive & Handover"]
  }
];

// -------------------------------------------------------------
// SERVICES
// -------------------------------------------------------------
export const studioServices: Service[] = [
  {
    id: "residential",
    number: "01",
    name: "Architectural Design (Residential)",
    description: "Custom single-family homes, private villas, hillside retreats, and multi-generational estates crafted around client rituals.",
    highlights: ["Custom Concept to Turnover", "Passive Energy Optimization", "Landscape & Courtyard Integration"]
  },
  {
    id: "commercial",
    number: "02",
    name: "Commercial & Cultural Architecture",
    description: "Distinctive civic spaces, boutique hospitality, cultural galleries, and corporate headquarters with landmark presence.",
    highlights: ["Brand Spatial Identity", "High Foot-Traffic Optimization", "Urban Context Sensitivity"]
  },
  {
    id: "interior",
    number: "03",
    name: "Interior Architecture & Spatial Curation",
    description: "Total architectural interiors: bespoke millwork, curated lighting schemes, material transitions, and artisanal fixtures.",
    highlights: ["Integrated Cabinetry & Detailing", "Acoustic & Lighting Design", "Material Tactility Curation"]
  },
  {
    id: "adaptive",
    number: "04",
    name: "Adaptive Reuse & Conservation",
    description: "Breathing contemporary relevance into heritage structures through surgical interventions, seismic retrofits, and modern envelopes.",
    highlights: ["Historic Fabric Assessment", "Structural Hybrid Systems", "Sustainable Embodied Carbon"]
  }
];

// -------------------------------------------------------------
// AWARDS & CITATIONS
// -------------------------------------------------------------
export const studioAwards: Award[] = [
  { year: "2024", award: "Excellence in Tropical Residential Architecture", project: "Monolith Pavilion", category: "Residential" },
  { year: "2023", award: "Young Architect Citation of Honor", project: "Studio Body of Work", category: "Honorary" },
  { year: "2023", award: "Civic Space Finalist", project: "Apex Cultural & Design Hub", category: "Cultural" },
  { year: "2022", award: "Sustainable Tectonics Design Prize", project: "Komorebi Sanctuary", category: "Sustainability" },
  { year: "2021", award: "Innovative Residential Spatial Planning", project: "Vessel Courtyard House", category: "Residential" }
];
